import { VoiceProduct } from '@shared/models/voice-product.model';
import { Selector } from '@ngxs/store';
import { CustomerProductMbbSelectors } from '../../modules/mbb/states/customer-product-mbb.selectors';
import { CustomerProductSelectors } from '../customer-product/customer-product.selectors';
import { MbbProduct } from '../../modules/mbb/models/mbb-product.model';
import { CustomerProductsAccessorySelectors } from '../accessory-product/accessory-product.selectors';
import { CustomerProductMobilePhoneSelectors } from '../customer-product-mobilephone/customer-product-mobilephone.selectors';
import { CustomerServiceProductSelectors } from '../customer-service-product/customer-service-product.selectors';
import { HardwareProduct } from '@shared/models/hardware-product.model';
import { MobilePhoneProduct } from '@sales/models/mobilephone-product';
import { CustomerServiceProduct } from '@shared/models/customer-service-product';
import { DiegoShoppingCart } from '@sales/models/shopping-cart.model';
import { MobilePaymentType } from '@sales/enums/mobile-payment-type';
import { PaymentSelectors } from '../payment/payment.selectors';
import { PaymentType } from '@shared/enums/payment-type';
import { MobilePhoneProductHelperService } from '@sales/services/mobile-phone-product-helper.service';
import { MobilePhoneBundle } from '@sales/models/mobile-phone-bundle.model';
import { UpfrontPaymentProducts } from '@app/sales/models/upfront-payment-products';
import { TradeInSelectors } from '../customer-product-trade-in/customer-product-trade-in.selectors';
import { TradeInProduct } from '@app/sales/models/trade-in-product.model';
import { AccessoryBundle } from '@app/sales/models/accessory-bundle.model';
import { PortingWindowsResponse } from '@app/sales/modules/subscription/services/porting.service';
import { CustomerSelectors } from '@app/sales/modules/details/states/customer.selectors';
import { orderRequiresBankIdVerification } from '@app/sales/bank-id-utils';
import { DealerStateSelectors } from '@app/core/states/dealer/dealer-state.selectors';
import { VoucherStateSelectors } from '../voucher/voucher.selectors';
import { AdditionalServiceVoice } from '@app/sales/modules/subscription/models/additional-service-voice.model';

export class ShoppingCartSelectors {
  @Selector([
    CustomerProductSelectors.newAndChangedVoiceProducts,
    CustomerProductMbbSelectors.newAndChangedMbbProducts,
    CustomerProductsAccessorySelectors.newAccessoryProducts,
    CustomerProductMobilePhoneSelectors.mobilePhoneProducts,
    CustomerProductMobilePhoneSelectors.getMobilePhoneBundles,
    CustomerServiceProductSelectors.getCustomerServiceProductsAdded,
    TradeInSelectors.tradeInProduct,
    TradeInSelectors.hasAcceptedTradeInVoucher,
    CustomerProductsAccessorySelectors.getAccessoryBundles,
  ])
  static shoppingCart(
    voiceProducts: VoiceProduct[],
    mbbProducts: MbbProduct[],
    accessoryProducts: HardwareProduct[],
    mobilePhoneProducts: MobilePhoneProduct[],
    mobilePhoneBundles: MobilePhoneBundle[],
    customerServiceProducts: CustomerServiceProduct[],
    tradeInProduct: TradeInProduct,
    hasAcceptedTradeInVoucher: boolean,
    accessoryBundles: AccessoryBundle[]
  ): DiegoShoppingCart {
    return {
      voiceProducts: voiceProducts.filter(x => !Boolean(x.mobileProductId || x.accessoryProductId)),
      mbbProducts: mbbProducts,
      accessoryProducts: accessoryProducts.filter(x => !Boolean(x.voiceProductId)),
      accessoryBundles: accessoryBundles,
      mobilePhoneProducts: mobilePhoneProducts.filter(x => !Boolean(x.voiceProductId)),
      mobilePhoneBundles: mobilePhoneBundles,
      customerServiceProducts: customerServiceProducts,
      tradeInProduct: hasAcceptedTradeInVoucher ? tradeInProduct : null,
    };
  }

  @Selector([
    CustomerProductSelectors.newAndChangedVoiceProducts,
    CustomerProductMbbSelectors.newAndChangedMbbProducts,
    CustomerProductsAccessorySelectors.newAccessoryProducts,
    CustomerProductMobilePhoneSelectors.mobilePhoneProducts,
    CustomerProductMobilePhoneSelectors.getMobilePhoneBundles,
    CustomerServiceProductSelectors.getCustomerServiceProductsAdded,
    TradeInSelectors.hasAcceptedTradeInVoucher,
  ])
  static numberOfItemsInShoppingCart(
    voiceProducts: VoiceProduct[],
    mbbProducts: MbbProduct[],
    accessoryProducts: HardwareProduct[],
    mobilePhoneProducts: MobilePhoneProduct[],
    mobilePhoneBundles: MobilePhoneBundle[],
    customerServiceProducts: CustomerServiceProduct[],
    hasAcceptedTradeInVoucher: boolean
  ): number {
    return (
      voiceProducts.filter(x => !Boolean(x.mobileProductId)).length +
      mbbProducts.length +
      accessoryProducts.length +
      mobilePhoneProducts.filter(x => !Boolean(x.voiceProductId)).length +
      mobilePhoneBundles.length +
      customerServiceProducts.length +
      (hasAcceptedTradeInVoucher ? 1 : 0)
    );
  }

  @Selector([
    CustomerProductSelectors.newAndChangedVoiceProducts,
    CustomerProductMbbSelectors.newAndChangedMbbProducts,
    CustomerProductsAccessorySelectors.newAccessoryProducts,
    CustomerProductMobilePhoneSelectors.mobilePhoneProducts,
    CustomerServiceProductSelectors.getCustomerServiceProductsAdded,
    TradeInSelectors.hasAcceptedTradeInVoucher,
    CustomerSelectors.hasCustomerInfoChanged,
  ])
  static isShoppingCartEmpty(
    voiceProducts: VoiceProduct[],
    mbbProducts: MbbProduct[],
    accessoryProducts: HardwareProduct[],
    mobilePhoneProducts: MobilePhoneProduct[],
    customerServiceProducts: CustomerServiceProduct[],
    hasAcceptedTradeInVoucher: boolean,
    hasCustomerInfoChanged: boolean
  ): boolean {
    return (
      voiceProducts.length +
        mbbProducts.length +
        accessoryProducts.length +
        mobilePhoneProducts.length +
        customerServiceProducts.length ===
        0 &&
      !hasAcceptedTradeInVoucher &&
      !hasCustomerInfoChanged
    );
  }

  @Selector([
    CustomerProductSelectors.getAllVoiceProducts,
    CustomerProductSelectors.newAndChangedVoiceProducts,
    CustomerProductMbbSelectors.newAndChangedMbbProducts,
    CustomerProductMobilePhoneSelectors.mobilePhoneProducts,
    CustomerServiceProductSelectors.getCustomerServiceProductsAdded,
  ])
  static shoppingCartMonthlyPrice(
    allVoiceProducts: VoiceProduct[],
    newAndChangedVoiceProducts: VoiceProduct[],
    mbbProducts: MbbProduct[],
    mobilePhoneProducts: MobilePhoneProduct[],
    customerServiceProducts: CustomerServiceProduct[]
  ): number {
    const voiceAmount: number = newAndChangedVoiceProducts
      .filter((voiceProduct: VoiceProduct) => Boolean(voiceProduct.offering.id))
      .map((voiceProduct: VoiceProduct) => voiceProduct.offering.monthlyPrice.price.includingVAT)
      .reduce((prev: number, current: number) => prev + current, 0);

    const additionalServiceAmount = newAndChangedVoiceProducts.reduce((acc, item) => {
      const amount = VoiceProduct.addedServices(item)
        .map(service => service.price.includingVAT)
        .reduce((prev, current) => prev + current, 0);
      return acc + amount;
    }, 0);

    const mbbAmount: number = mbbProducts
      .filter((mbbProduct: MbbProduct) => Boolean(mbbProduct.mbbSubscription.id))
      .map((mbbProduct: MbbProduct) => mbbProduct.mbbSubscription.monthlyPrice.price.includingVAT)
      .reduce((prev: number, current: number) => prev + current, 0);

    const insuranceAmount = mobilePhoneProducts
      .filter(x => Boolean(x.insuranceOption))
      .map(x => x.insuranceOption.priceInformation.price.includingVAT)
      .reduce((prev: number, current: number) => prev + current, 0);

    let mobilePhoneEquipmentUpgradeAmount = 0;

    mobilePhoneEquipmentUpgradeAmount = mobilePhoneProducts
      .filter(x => x.paymentType === MobilePaymentType.EquipmentUpgrade)
      .map(x => MobilePhoneProductHelperService.getMobilbyttePrice(x).monthlyPrice.includingVAT)
      .reduce((prev: number, current: number) => prev + current, 0);

    const customerServiceOfferingsAmount = customerServiceProducts
      .filter(x => !x.isExistingSubscription)
      .map(x => x.customerServiceOffering.monthlyPrice.includingVAT)
      .reduce((prev: number, current: number) => prev + current, 0);

    return (
      voiceAmount +
      additionalServiceAmount +
      mbbAmount +
      insuranceAmount +
      mobilePhoneEquipmentUpgradeAmount +
      customerServiceOfferingsAmount
    );
  }

  @Selector([
    CustomerProductSelectors.getAllVoiceProducts,
    PaymentSelectors.productsToBePaidNowByCustomer,
    TradeInSelectors.totalPrice,
    TradeInSelectors.hasAcceptedTradeInVoucher,
    VoucherStateSelectors.voucherAmount,
    DealerStateSelectors.canSellHardwareWithLogistics,
  ])
  static shoppingCartPayNowPrice(
    allVoiceProducts: VoiceProduct[],
    { mbbProducts, accessoryProducts, mobilePhoneProducts }: UpfrontPaymentProducts,
    tradeInPrice: number,
    hasAcceptedTradeInVoucher: boolean,
    voucherAmount: number,
    canSellHardwareWithLogistics: boolean
  ): number {
    const accessoryAmount = accessoryProducts
      .map(accessoryProduct => {
        if (accessoryProduct.equipment.isManualRegistration) {
          return 0;
        } else if (!!accessoryProduct.selectedSubscription) {
          return accessoryProduct.selectedSubscription.hardwareOfferingPriceWithSubscription?.amountIncludingVAT || 0;
        } else {
          return accessoryProduct.equipment.price.includingVAT;
        }
      })
      .reduce((prev: number, current: number) => prev + current, 0);

    const mbbAmount: number = canSellHardwareWithLogistics
      ? mbbProducts
          .map((mbbProduct: MbbProduct) => mbbProduct.selectedBundle?.hardwarePrice.includingVAT)
          .reduce((prev: number, current: number) => prev + current, 0)
      : 0;

    const mobilePhoneAmount: number = mobilePhoneProducts
      .map(
        (mobilePhoneProduct: MobilePhoneProduct) =>
          MobilePhoneProductHelperService.getMobilePhonePrice(mobilePhoneProduct, allVoiceProducts).totalPrice
            ?.includingVAT ?? 0
      )
      .reduce((prev: number, current: number) => prev + current, 0);

    const tradeInAmount = hasAcceptedTradeInVoucher ? tradeInPrice || 0 : 0;

    return accessoryAmount + mbbAmount + mobilePhoneAmount - tradeInAmount - voucherAmount;
  }

  @Selector([ShoppingCartSelectors.shoppingCart])
  static shoppingCartPayWithNextInvoicePrice({ accessoryProducts, mbbProducts }: DiegoShoppingCart): number {
    const accessoryAmount = accessoryProducts
      .filter((accessoryProduct: HardwareProduct) => accessoryProduct.paymentType === PaymentType.PayWithNextInvoice)
      .map((accessoryProduct: HardwareProduct) => accessoryProduct.equipment.price.includingVAT)
      .reduce((prev: number, current: number) => prev + current, 0);

    const mbbAmount: number = mbbProducts
      .filter((mbbProduct: MbbProduct) => mbbProduct.paymentType === PaymentType.PayWithNextInvoice)
      .map((mbbProduct: MbbProduct) => mbbProduct.selectedBundle?.hardwarePrice.includingVAT)
      .reduce((prev: number, current: number) => prev + current, 0);

    return accessoryAmount + mbbAmount;
  }

  //Needs to be reflected in SPS as well
  @Selector([ShoppingCartSelectors.shoppingCart, ShoppingCartSelectors.hasShoppingCartMobilBytteProduct])
  static isContractRequired(shoppingCart: DiegoShoppingCart, hasShoppingCartMobilBytteProduct: boolean): boolean {
    return (
      hasShoppingCartMobilBytteProduct ||
      shoppingCart.voiceProducts.some((voiceProduct: VoiceProduct) => Boolean(voiceProduct.offering.id)) ||
      shoppingCart.mbbProducts.some((mbbProduct: MbbProduct) => Boolean(mbbProduct.mbbSubscription.id)) ||
      Boolean(shoppingCart.tradeInProduct?.tradeInVoucher?.tradeInId) ||
      shoppingCart.mobilePhoneBundles.some((mobilePhoneBundle: MobilePhoneBundle) =>
        Boolean(mobilePhoneBundle.connectedVoiceProduct?.offering.id)
      ) ||
      shoppingCart.accessoryBundles.some((accessoryBundle: AccessoryBundle) =>
        Boolean(accessoryBundle.connectedVoiceProduct?.offering.id)
      )
    );
  }

  @Selector([
    CustomerProductMobilePhoneSelectors.mobilePhoneProducts,
    CustomerProductsAccessorySelectors.newAccessoryProducts,
  ])
  static hasShoppingCartPcmProduct(
    mobilePhoneProducts: MobilePhoneProduct[],
    accessoryProducts: HardwareProduct[]
  ): boolean {
    return mobilePhoneProducts.length > 0 || accessoryProducts.length > 0;
  }

  @Selector([CustomerProductMbbSelectors.newAndChangedMbbProducts])
  static hasShoppingCartMbbProduct(mbbProducts: MbbProduct[]): boolean {
    return mbbProducts.length > 0;
  }

  @Selector([CustomerProductSelectors.newAndChangedVoiceProducts])
  static hasShoppingCartESimProduct(voiceProducts: VoiceProduct[]): boolean {
    return voiceProducts.some((voiceProduct: VoiceProduct) => voiceProduct.phoneNumber?.isESim);
  }

  @Selector([CustomerProductMobilePhoneSelectors.mobilePhoneProducts])
  static hasShoppingCartMobilBytteProduct(mobilePhoneProducts: MobilePhoneProduct[]): boolean {
    return mobilePhoneProducts.some(
      (mobilePhoneProduct: MobilePhoneProduct) => mobilePhoneProduct.paymentType === MobilePaymentType.EquipmentUpgrade
    );
  }

  @Selector([
    DealerStateSelectors.hasPhysicalSigningClaim,
    CustomerSelectors.isCustomerVerifiedWithBankId,
    ShoppingCartSelectors.hasShoppingCartMobilBytteProduct,
  ])
  static hasNewMobilbytteAgreementInCartAndIsntBankIdVerified(
    hasPhysicalSigningClaim: boolean,
    isCustomerVerifiedWithBankId: boolean,
    hasShoppingCartMobilBytteProduct: boolean
  ): boolean {
    return hasPhysicalSigningClaim && hasShoppingCartMobilBytteProduct && !isCustomerVerifiedWithBankId;
  }

  @Selector([CustomerProductSelectors.newAndChangedVoiceProducts])
  static hasShoppingCartLogisticsVoiceProduct(voiceProducts: VoiceProduct[]): boolean {
    return voiceProducts.some((voiceProduct: VoiceProduct) => voiceProduct.phoneNumber?.orderSim);
  }

  @Selector([CustomerProductSelectors.newAndChangedVoiceProducts, CustomerProductSelectors.allowedPortingDates])
  static hasShoppingCartESimWithPortingTimeEarlierThanLogisticsVoiceProductAllows(
    voiceProducts: VoiceProduct[],
    portingDates: PortingWindowsResponse
  ): boolean {
    if (!this.hasShoppingCartLogisticsVoiceProduct(voiceProducts)) return false;

    const earliestPortingDateWithLogistics = new Date(portingDates?.withLogistics[0]?.date);
    if (Number.isNaN(earliestPortingDateWithLogistics)) return false;

    const hasESimProductToDelay = voiceProducts.some((voiceProduct: VoiceProduct) => {
      if (!voiceProduct.phoneNumber) return;

      const isESim = voiceProduct.phoneNumber.isESim;
      const activationDateTime = new Date(voiceProduct.phoneNumber.activationDateTime);

      if (Number.isNaN(activationDateTime.valueOf)) return;

      return isESim && activationDateTime < earliestPortingDateWithLogistics;
    });

    return hasESimProductToDelay;
  }

  @Selector([CustomerProductSelectors.newAndChangedVoiceProducts])
  static getAdditionalSims(voiceProducts: VoiceProduct[]): AdditionalServiceVoice[] {
    return voiceProducts
      .flatMap(vp => VoiceProduct.addedServices(vp))
      .filter(service => service.isDataSimCard || service.isTwinSimCard);
  }

  @Selector([ShoppingCartSelectors.getAdditionalSims])
  static hasInconsistentAdditionalSimIcc(additionalSims: AdditionalServiceVoice[]): boolean {
    if (additionalSims.length < 2) {
      return false;
    }

    const hasSimByMail = additionalSims.some(s => s.orderSim);
    const hasSimInStore = additionalSims.some(s => s.orderSim === false);

    return hasSimByMail && hasSimInStore;
  }

  @Selector([ShoppingCartSelectors.getAdditionalSims])
  static hasAdditionalSimMissingIcc(additionalSims: AdditionalServiceVoice[]): boolean {
    return additionalSims.some(s => s.orderSim === false && !s.icc);
  }

  @Selector([ShoppingCartSelectors.getAdditionalSims])
  static hasDuplicateAdditionalSimIcc(additionalSims: AdditionalServiceVoice[]): boolean {
    const iccs = additionalSims.map(service => service.icc).filter(icc => !!icc);

    return iccs.length !== new Set(iccs).size;
  }

  @Selector([
    CustomerProductMobilePhoneSelectors.getNewMobilePhoneProducts,
    CustomerProductMbbSelectors.getNewMbbProducts,
    CustomerProductsAccessorySelectors.newAccessoryProducts,
  ])
  static imeisInShoppingCart(
    mobilePhoneProducts: MobilePhoneProduct[],
    mbbProducts: MbbProduct[],
    accessoryProducts: HardwareProduct[]
  ): string[] {
    const newMobilePhoneImeis = mobilePhoneProducts.map(product => {
      return product.imei;
    });
    const newMbbImeis = mbbProducts.map(product => {
      return product.imei;
    });
    const newAccessoryImeis = accessoryProducts.map(product => {
      return product.equipment?.imei;
    });
    const allImeis = [...newMobilePhoneImeis, ...newMbbImeis, ...newAccessoryImeis].filter(imei => !!imei);

    return allImeis;
  }

  @Selector([ShoppingCartSelectors.imeisInShoppingCart])
  static hasDuplicateImeisInShoppingCart(newImeis: string[]): boolean {
    return newImeis.some((imei, index) => newImeis.indexOf(imei) !== index);
  }

  @Selector([
    CustomerProductSelectors.newAndChangedVoiceProducts,
    CustomerProductMbbSelectors.newAndChangedMbbProducts,
    CustomerProductMobilePhoneSelectors.mobilePhoneProducts,
    CustomerProductsAccessorySelectors.newAccessoryProducts,
    DealerStateSelectors.hasPhysicalSigningClaim,
    DealerStateSelectors.canUseBankIdForIdentificationClaim,
    CustomerProductSelectors.customerHasSecretNumber,
  ])
  static isBankIdVerificationRequiredForShoppingCart(
    voiceProducts: VoiceProduct[],
    mbbProducts: MbbProduct[],
    mobilePhoneProducts: MobilePhoneProduct[],
    accessoryProducts: HardwareProduct[],
    hasPhysicalSigningClaim: boolean,
    canUseBankIdForIdentificationClaim: boolean,
    customerHasSecretNumber: boolean
  ): boolean {
    const isNotApplicable = hasPhysicalSigningClaim;
    if (!canUseBankIdForIdentificationClaim || isNotApplicable) {
      return false;
    }
    const allProducts = [...voiceProducts, ...mbbProducts, ...mobilePhoneProducts, ...accessoryProducts];

    return orderRequiresBankIdVerification(allProducts, customerHasSecretNumber);
  }
}
